import moment from 'moment';
import { combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';

import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import {
  advancementTypes,
  intl, // navigateToRoster, // navigateToPurchaseOrderDetails,
} from '@modules/shared';
import { toastService } from '@modules/toasts';
import { catchAndReport } from '@modules/utils/rxjs/operators';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { userIdSel as loggedInUserIdSel } from '@user';

// import { POStatusTypes } from '../constants';
import {
  ADD_ITEMS_TO_CART_REQUEST,
  ADD_TO_PURCHASE_ORDER_REQUEST,
  CREATE_PURCHASE_ORDER_REQUEST,
  DELETE_FROM_ORDER_AND_RETRY,
  DELETE_FROM_PURCHASE_ORDER_REQUEST,
  DELETE_PURCHASE_ORDER_REQUEST,
  DOWNLOAD_POCKET_CERTIFICATE_PDF_REQUEST,
  FETCH_ALL_PURCHASE_ORDERS_REQUEST,
  FETCH_DETAILS_AND_RETRY_REQUEST,
  FETCH_PURCHASE_ORDER_DETAILS_REQUEST,
  SET_CART_TOKEN,
  UPDATE_PURCHASE_ORDER_REQUEST,
  addItemsToCartError,
  addItemsToCartRequest,
  addItemsToCartResponse,
  addToPurchaseOrderError,
  addToPurchaseOrderResponse,
  createPurchaseOrderError,
  createPurchaseOrderResponse,
  deleteFromOrderAndRetryError,
  deleteFromPurchaseOrderError,
  deleteFromPurchaseOrderResponse,
  deletePurchaseOrderError,
  deletePurchaseOrderResponse,
  downloadPocketCertificatePDFError,
  downloadPocketCertificatePDFResposne,
  fetchAllPurchaseOrdersError,
  fetchAllPurchaseOrdersRequest,
  fetchAllPurchaseOrdersResponse,
  fetchDetailsAndRetryError,
  fetchDetailsAndRetryRequest,
  fetchPurchaseOrderDetailsError,
  fetchPurchaseOrderDetailsRequest,
  fetchPurchaseOrderDetailsResponse,
  setCartToken,
  setCartTokenError,
  setCartTokenResponse,
  setCreatePurchaseOrderLoading,
  setPurchaseOrderId,
  setUpdatePurchaseOrderLoading,
  updatePurchaseOrderError,
  updatePurchaseOrderResponse,
} from './actions';
import { itemsForCartSel } from './selectors';
import { cartService, esbServices } from './services';

const guestCartEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(ADD_ITEMS_TO_CART_REQUEST),
    mergeMap(() => {
      const state = state$.value;
      const items = itemsForCartSel(state);
      const organizationGuid = organizationGuidSel(state);
      const poid =
        state?.advancement.purchaseOrders.selectedPurchaseOrderId || null;

      // first create a cart and get the cart token
      return cartService.createGuestCart$().pipe(
        mergeMap(cartToken =>
          // then update cart with desired items
          cartService.updateGuestCart$({ cartToken, items }).pipe(
            mergeMap(data => {
              const actions = [
                of(addItemsToCartResponse(data)),
                of(
                  setCartToken({
                    cartToken: cartToken,
                    organizationGuid,
                    poid,
                  }),
                ),
              ];

              return Observable.concat(...actions);
            }),
            catchAndReport(err => {
              let errorMessage =
                err.response?.data?.message || err.message || 'Unknown error';
              return of(addItemsToCartError(errorMessage));
            }),
          ),
        ),
        catchError(error => Observable.of(addItemsToCartError(error))),
      );
    }),
  );

const setCartTokenEpic$ = action$ =>
  action$.ofType(SET_CART_TOKEN).switchMap(({ payload }) => {
    const { cartToken, organizationGuid, poid } = payload;

    return esbServices
      .updatePurchaseOrder$({ cartToken }, organizationGuid, poid)
      .map(setCartTokenResponse)
      .catchAndReport(err => of(setCartTokenError(err)));
  });

const fetchAllPurchaseOrdersEpic$ = (action$, state$) =>
  action$.ofType(FETCH_ALL_PURCHASE_ORDERS_REQUEST).switchMap(() => {
    const state = state$.value;
    const organizationGuid = organizationGuidSel(state);
    return esbServices
      .getAllPurchaseOrders$(organizationGuid)
      .map(fetchAllPurchaseOrdersResponse)
      .catchAndReport(err => Observable.of(fetchAllPurchaseOrdersError(err)));
  });

const createPurchaseOrderEpic$ = (action$, state$) =>
  action$.ofType(CREATE_PURCHASE_ORDER_REQUEST).switchMap(request => {
    const { payload: items } = request;
    const state = state$.value;
    const organizationGuid = organizationGuidSel(state);
    const userId = loggedInUserIdSel(state);
    const POdetails = {
      dateCreated: moment().format('YYYY-MM-DD'),
      userId: userId,
    };
    return esbServices
      .createPurchaseOrderWithItems$(POdetails, organizationGuid, items)
      .mergeMap(response =>
        Observable.concat(
          Observable.of(createPurchaseOrderResponse),
          Observable.of(setPurchaseOrderId(response.id)),
          Observable.of(fetchPurchaseOrderDetailsRequest(response.id)),
          Observable.of(fetchAllPurchaseOrdersRequest()),
          Observable.of(setCreatePurchaseOrderLoading(false)),
        ),
      )
      .catchAndReport(err =>
        Observable.concat(
          Observable.of(createPurchaseOrderError(err)),
          Observable.of(setCreatePurchaseOrderLoading(false)),
        ),
      );
  });

const fetchPurchaseOrderDetailsEpic$ = (action$, state$) =>
  action$
    .ofType(FETCH_PURCHASE_ORDER_DETAILS_REQUEST)
    .switchMap(({ payload }) => {
      const state = state$.value;
      const poid = payload;
      const organizationGuid = organizationGuidSel(state);
      return esbServices
        .getPurchaseOrderDetails$(organizationGuid, poid)
        .mergeMap(response =>
          Observable.concat(
            Observable.of(fetchPurchaseOrderDetailsResponse(response)),
            Observable.of(setCreatePurchaseOrderLoading(false)),
          ),
        )
        .catchAndReport(err =>
          Observable.of(fetchPurchaseOrderDetailsError(err)),
        );
    });

const updatePurchaseOrderEpic$ = (action$, state$) =>
  action$.ofType(UPDATE_PURCHASE_ORDER_REQUEST).switchMap(({ payload }) => {
    const state = state$.value;
    const { id, data } = payload;
    const organizationGuid = organizationGuidSel(state);

    return esbServices.updatePurchaseOrder$(data, organizationGuid, id).pipe(
      tap(() =>
        toastService.success(
          intl.formatMessage({
            id: 'PurchaseOrder.PODetailsPage.success',
          }),
        ),
      ),
      mergeMap(response =>
        Observable.from([
          updatePurchaseOrderResponse(response),
          fetchPurchaseOrderDetailsRequest(id),
          fetchAllPurchaseOrdersRequest(),
          setUpdatePurchaseOrderLoading(false),
          esbApi.util.invalidateTags([
            tagFactory.PurchaseOrders.purchaseOrderDetails(id),
          ]),
        ]),
      ),
      catchAndReport(err => Observable.of(updatePurchaseOrderError(err))),
    );
  });

const deleteItemFromOrderEpic$ = (action$, state$) =>
  action$
    .ofType(DELETE_FROM_PURCHASE_ORDER_REQUEST)
    .switchMap(({ payload }) => {
      const state = state$.value;
      const { poid, data } = payload;
      const organizationGuid = organizationGuidSel(state);

      return esbServices
        .deleteFromPurchaseOrder$(poid, organizationGuid, data)
        .pipe(
          tap(() =>
            toastService.success(
              intl.formatMessage({
                id: 'PurchaseOrder.PODetailsPage.success',
              }),
            ),
          ),
          mergeMap(response =>
            Observable.from([
              deleteFromPurchaseOrderResponse(response),
              fetchPurchaseOrderDetailsRequest(poid),
              setUpdatePurchaseOrderLoading(false),
            ]),
          ),
          catchAndReport(err =>
            Observable.of(deleteFromPurchaseOrderError(err)),
          ),
        );
    });

const addToPurchaseOrderEpic$ = (action$, state$) =>
  action$.ofType(ADD_TO_PURCHASE_ORDER_REQUEST).switchMap(({ payload }) => {
    const state = state$.value;
    const { poid, selectedItems } = payload;
    const organizationGuid = organizationGuidSel(state);
    const data = selectedItems.map(item => {
      const dataItem = {
        userId: item.userId,
        advancementType: item.advancementType,
        advancementId: item.id,
      };
      if (item.advancementType === advancementTypes.AWARDS) {
        dataItem.userAdvancementId = item.userAwardId;
      }
      return dataItem;
    });

    return esbServices.addToPurchaseOrder$(poid, organizationGuid, data).pipe(
      tap(() =>
        toastService.success(
          intl.formatMessage({
            id: 'PurchaseOrder.PODetailsPage.success',
          }),
        ),
      ),
      mergeMap(response =>
        Observable.from([
          addToPurchaseOrderResponse(response),
          fetchPurchaseOrderDetailsRequest(poid),
          setUpdatePurchaseOrderLoading(false),
        ]),
      ),
      catchAndReport(err => Observable.of(addToPurchaseOrderError(err))),
    );
  });

const deleteFromOrderAndRetryEpic$ = (action$, state$) =>
  action$.ofType(DELETE_FROM_ORDER_AND_RETRY).switchMap(({ payload }) => {
    const state = state$.value;
    const { poid, data } = payload;
    const organizationGuid = organizationGuidSel(state);
    return esbServices
      .deleteFromPurchaseOrder$(poid, organizationGuid, data)
      .mergeMap(response =>
        Observable.from([
          deleteFromPurchaseOrderResponse(response),
          fetchDetailsAndRetryRequest(poid),
          setUpdatePurchaseOrderLoading(false),
          esbApi.util.invalidateTags([
            tagFactory.PurchaseOrders.purchaseOrderDetails(poid),
          ]),
        ]),
      )
      .catchAndReport(err => Observable.of(deleteFromOrderAndRetryError(err)));
  });

const fetchDetailsAndRetryEpic$ = (action$, state$) =>
  action$.ofType(FETCH_DETAILS_AND_RETRY_REQUEST).switchMap(({ payload }) => {
    const state = state$.value;
    const poid = payload;
    const organizationGuid = organizationGuidSel(state);
    return esbServices
      .getPurchaseOrderDetails$(organizationGuid, poid)
      .mergeMap(response =>
        Observable.from([
          fetchPurchaseOrderDetailsResponse(response),
          addItemsToCartRequest(),
        ]),
      )
      .catchAndReport(err => Observable.of(fetchDetailsAndRetryError(err)));
  });

const deletePurchaseOrderEpic$ = (action$, state$) =>
  action$.ofType(DELETE_PURCHASE_ORDER_REQUEST).switchMap(({ payload }) => {
    const state = state$.value;
    const organizationGuid = organizationGuidSel(state);
    const { id } = payload;

    return esbServices.deletePurchaseOrder$(id, organizationGuid).pipe(
      tap(() =>
        toastService.success(
          intl.formatMessage({
            id: 'PurchaseOrder.PODetailsPage.delete.success',
          }),
        ),
      ),
      mergeMap(response =>
        Observable.concat(
          of(deletePurchaseOrderResponse(response)),
          of(fetchAllPurchaseOrdersRequest()),
          of(setUpdatePurchaseOrderLoading(false)),
        ),
      ),
      catchAndReport(err => Observable.of(deletePurchaseOrderError(err))),
    );
  });

const downloadPocketCertificatePDFEpic$ = action$ =>
  action$
    .ofType(DOWNLOAD_POCKET_CERTIFICATE_PDF_REQUEST)
    .switchMap(({ payload }) => {
      const { poid, unitId, rankId } = payload;
      // return esbServices.downloadPocketCertificatePDF$(poid, unitId, rankId)
      //   .map(downloadPocketCertificatePDFResposne(poid))
      //   .catchAndReport(err =>
      //     Observable.of(downloadPocketCertificatePDFError(err)),
      //   );

      return esbServices
        .downloadPocketCertificatePDF$(poid, unitId, rankId)
        .pipe(
          tap(() =>
            toastService.success(
              intl.formatMessage({
                id: 'PurchaseOrder.PODetailsPage.delete.success',
              }),
            ),
          ),
          mergeMap(() =>
            Observable.concat(of(downloadPocketCertificatePDFResposne(poid))),
          ),
          catchAndReport(err =>
            Observable.of(downloadPocketCertificatePDFError(err)),
          ),
        );
    });

export default combineEpics(
  guestCartEpic$,
  setCartTokenEpic$,
  fetchAllPurchaseOrdersEpic$,
  createPurchaseOrderEpic$,
  fetchPurchaseOrderDetailsEpic$,
  updatePurchaseOrderEpic$,
  deleteItemFromOrderEpic$,
  addToPurchaseOrderEpic$,
  deleteFromOrderAndRetryEpic$,
  fetchDetailsAndRetryEpic$,
  deletePurchaseOrderEpic$,
  downloadPocketCertificatePDFEpic$,
);
